function normalizeText(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function uniqueValues(values = []) {
  const seen = new Set();

  return values.filter((value) => {
    const key = `${typeof value}:${String(value)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function decimalPlaces(value) {
  const text = String(value);
  const dotIndex = text.indexOf(".");
  return dotIndex === -1 ? 0 : text.length - dotIndex - 1;
}

function getStepError(type, step) {
  const value = String(step ?? "").trim();
  if (!value) return "";

  if (type === "I" && !/^[1-9]\d*$/.test(value)) {
    return "Integer step must be a positive whole number";
  }

  if (
    type === "F" &&
    (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(value) || Number(value) <= 0)
  ) {
    return "Float step must be a positive number";
  }

  if (type === "B" && value !== "1") {
    return "Binary range step must be 1";
  }

  return "";
}

function getRangeValueError(type, rangeValue) {
  const value = String(rangeValue ?? "").trim();
  if (!value) return "";

  if (
    type === "I" &&
    !(
      /^-?\d+\s*(-|\.\.|:)\s*-?\d+$/.test(value) ||
      /^-?\d+(\s*,\s*-?\d+)*$/.test(value)
    )
  ) {
    return "Integer ranges allow whole values only, for example 1-10 or 10,20,30,40";
  }

  if (
    type === "F" &&
    !(
      /^-?(?:\d+\.\d+|\.\d+)\s*(-|\.\.|:)\s*-?(?:\d+\.\d+|\.\d+)$/.test(
        value
      ) ||
      /^-?(?:\d+\.\d+|\.\d+)(\s*,\s*-?(?:\d+\.\d+|\.\d+))*$/.test(
        value
      )
    )
  ) {
    return "Float ranges require decimal values, for example 1.0-5.0 or 1.5,2.5";
  }

  if (type === "B" && !/^(0|1|T|nil)(\s*,\s*(0|1|T|nil))*$/i.test(value)) {
    return "Binary ranges allow only 0, 1, T or nil";
  }

  if (type === "S" && !/^[A-Za-z0-9_]+(\s*,\s*[A-Za-z0-9_]+)*$/.test(value)) {
    return "String ranges allow comma-separated letters, numbers and underscores only";
  }

  return "";
}

export function parseRangeValues(type, rangeValue, step) {
  const value = String(rangeValue ?? "").trim();
  if (!value || getRangeValueError(type, value) || getStepError(type, step)) {
    return [];
  }

  if (value.includes(",")) {
    return uniqueValues(
      value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    );
  }

  if (type !== "I" && type !== "F") return [value];

  const match = value.match(/^(-?\d+(?:\.\d+)?)\s*(-|\.\.|:)\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return [value];

  const start = Number(match[1]);
  const end = Number(match[3]);
  const stepNumber = String(step ?? "").trim() ? Number(step) : 1;
  const direction = start <= end ? 1 : -1;
  const distance = Math.abs(end - start);
  const count = Math.floor(distance / stepNumber + 1e-9);
  const precision =
    type === "F"
      ? Math.max(
          1,
          decimalPlaces(match[1]),
          decimalPlaces(match[3]),
          decimalPlaces(stepNumber)
        )
      : 0;
  const values = [];

  for (let index = 0; index <= count; index += 1) {
    const current = start + direction * stepNumber * index;
    values.push(
      type === "I"
        ? String(current)
        : Number(current).toFixed(precision)
    );
  }

  return uniqueValues(values);
}

export function validateRangeDefinition(type, rangeValue, step) {
  const stepError = getStepError(type, step);
  const rangeValueError = getRangeValueError(type, rangeValue);
  const values =
    stepError || rangeValueError
      ? []
      : parseRangeValues(type, rangeValue, step);

  return {
    isValid: !stepError && !rangeValueError,
    stepError,
    rangeValueError,
    values,
  };
}

function getTypeLabel(type) {
  if (type === "I") return "int";
  if (type === "F") return "float";
  if (type === "B") return "binary";
  if (type === "S") return "string";
  return type || "";
}

function buildSynchronizedSweep(rangeBucket) {
  const members = rangeBucket.members || [];
  const maxLength = Math.max(0, ...members.map((member) => member.values.length));
  const cases = [];

  for (let index = 0; index < maxLength; index += 1) {
    const values = {};

    for (const member of members) {
      values[member.param.name] =
        member.values.length === 1 ? member.values[0] : member.values[index];
    }

    cases.push(values);
  }

  return cases;
}

function combineRangeSweeps(rangeBuckets = []) {
  return rangeBuckets.reduce(
    (combinedCases, bucket) => {
      const bucketCases = buildSynchronizedSweep(bucket);
      const nextCases = [];

      for (const combinedCase of combinedCases) {
        for (const bucketCase of bucketCases) {
          nextCases.push({
            ...combinedCase,
            ...bucketCase,
          });
        }
      }

      return nextCases;
    },
    [{}]
  );
}

function setParameterError(errors, param, field, message) {
  const key = param.id || param.name;
  if (!key || !message) return;

  errors[key] = {
    ...(errors[key] || {}),
    [field]: message,
  };
}

function hasUnsynchronizedLengths(rangeBucket) {
  const varyingLengths = new Set(
    rangeBucket.members
      .map((member) => member.values.length)
      .filter((length) => length > 1)
  );

  return varyingLengths.size > 1;
}

function uniqueCases(rows) {
  const seen = new Set();

  return rows.filter((row) => {
    const key = `${row.group}:${JSON.stringify(row.values)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function buildRangePreviewTable(parameters = []) {
  const validParams = parameters.filter((param) =>
    String(param.name || "").trim()
  );
  const columns = validParams.map((param) => param.name);
  const typeRow = validParams.map((param) => getTypeLabel(param.type));
  const defaultValues = Object.fromEntries(
    validParams.map((param) => [
      param.name,
      String(param.defaultValue ?? param.defaultVal ?? param.value ?? ""),
    ])
  );
  const parameterErrors = {};
  const groups = new Map();

  validParams.forEach((param, paramIndex) => {
    const validation = validateRangeDefinition(
      param.type,
      param.rangeValue,
      param.step
    );

    setParameterError(parameterErrors, param, "step", validation.stepError);
    setParameterError(
      parameterErrors,
      param,
      "rangeValue",
      validation.rangeValueError
    );

    if (!validation.isValid || !validation.values.length) return;

    const groupName = normalizeText(param.groupName, "default");
    const explicitRangeName = normalizeText(param.rangeName);
    const displayRangeName =
      explicitRangeName || `${normalizeText(param.name, "parameter")}_range`;
    const groupKey = groupName.toLowerCase();
    const rangeKey = explicitRangeName
      ? explicitRangeName.toLowerCase()
      : `__unnamed_${param.id || param.name || paramIndex}`;

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        name: groupName,
        ranges: new Map(),
      });
    }

    const group = groups.get(groupKey);

    if (!group.ranges.has(rangeKey)) {
      group.ranges.set(rangeKey, {
        name: displayRangeName,
        members: [],
      });
    }

    group.ranges.get(rangeKey).members.push({
      param,
      values: validation.values,
    });
  });

  for (const group of groups.values()) {
    for (const rangeBucket of group.ranges.values()) {
      if (!hasUnsynchronizedLengths(rangeBucket)) continue;

      const lengths = rangeBucket.members
        .map((member) => member.values.length)
        .join(", ");
      const message = `Same GroupName and R-Name must have equal value counts, or one value for broadcasting. Current counts: ${lengths}`;

      for (const member of rangeBucket.members) {
        setParameterError(parameterErrors, member.param, "rangeValue", message);
      }
    }
  }

  const hasErrors = Object.keys(parameterErrors).length > 0;
  const rows = [];

  if (!hasErrors) {
    for (const group of groups.values()) {
      const rangeBuckets = Array.from(group.ranges.values());
      const combinedCases = combineRangeSweeps(rangeBuckets);
      const combinedRangeName =
        rangeBuckets.map((bucket) => bucket.name).join("_") || "range";

      combinedCases.forEach((caseValues, caseIndex) => {
        const sequence = String(caseIndex + 1).padStart(3, "0");

        rows.push({
          group: group.name,
          rangeName: `${combinedRangeName}_${sequence}`,
          values: {
            ...defaultValues,
            ...caseValues,
          },
        });
      });
    }
  }

  return {
    columns,
    typeRow,
    rows: uniqueCases(rows),
    parameterErrors,
    hasErrors,
  };
}
